import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "antd";
import type { TooltipRef } from "antd/es/tooltip";

type Anchor = { node: HTMLElement; length: string; left: number; top: number; width: number; height: number };

/** Hover state belongs to the page's tooltip, not to its text or edit controls. */
export function RheseLengthTooltip({ root, revision }: {
  root: React.RefObject<HTMLDivElement | null>;
  revision: unknown;
}) {
  const id = useId();
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const tooltip = useRef<TooltipRef>(null);
  const refresh = useRef<() => void>(() => {});
  useLayoutEffect(() => {
    const page = root.current;
    if (!page) return;
    const scrollRoot = page.closest("#project-root");
    let active: HTMLElement | null = null;
    let hovered: HTMLElement | null = null;
    let focused: HTMLElement | null = null;
    let dismissed: HTMLElement | null = null;
    let describedControl: HTMLElement | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let frame = 0;
    const find = (target: EventTarget | null, fromControl = false) => {
      if (!(target instanceof Element) || !page.contains(target)) return null;
      const node = target.closest<HTMLElement>("[data-rhese-length]");
      return node || (fromControl ? target.closest("button[data-rhese-edit-id]")?.parentElement
        ?.querySelector<HTMLElement>("[data-rhese-length]") || null : null);
    };
    const popupContains = (target: EventTarget | null) => active && target instanceof Node &&
      !!document.getElementById(id)?.closest(".ant-tooltip")?.contains(target);
    const describe = (node: HTMLElement, add: boolean) => {
      const ids = new Set((node.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean));
      if (add) ids.add(id); else ids.delete(id);
      if (ids.size) node.setAttribute("aria-describedby", [...ids].join(" ")); else node.removeAttribute("aria-describedby");
    };
    const hide = () => {
      clearTimeout(timer);
      observer.disconnect();
      if (active) describe(active, false);
      if (describedControl) describe(describedControl, false);
      describedControl = null;
      active = null; setAnchor(null);
    };
    const update = () => {
      if (!active) return;
      if (!active.isConnected || !page.contains(active)) { hide(); return; }
      const r = active.getBoundingClientRect();
      const clip = scrollRoot?.getBoundingClientRect();
      if (r.bottom <= Math.max(0, clip?.top || 0) || r.top >= Math.min(innerHeight, clip?.bottom ?? innerHeight) ||
        r.right <= 0 || r.left >= innerWidth) { hide(); return; }
      const next = { node: active, length: active.dataset.rheseLength || "0", left: r.left, top: r.top, width: r.width, height: r.height };
      setAnchor(old => old && old.node === next.node && old.length === next.length &&
        old.left === next.left && old.top === next.top && old.width === next.width && old.height === next.height ? old : next);
    };
    const reposition = () => { if (active && !frame) frame = requestAnimationFrame(() => { frame = 0; update(); }); };
    const observer = new ResizeObserver(reposition);
    const show = (node: HTMLElement) => {
      if (describedControl) describe(describedControl, false);
      describedControl = find(document.activeElement, true) === node
        ? document.activeElement?.closest<HTMLElement>("button[data-rhese-edit-id]") || null : null;
      if (describedControl) describe(describedControl, true);
      if (active !== node) {
        if (active) describe(active, false);
        observer.disconnect();
        active = node; describe(node, true);
        observer.observe(node); observer.observe(page);
      }
      update();
    };
    const choose = (node: HTMLElement | null, delay = 100) => {
      clearTimeout(timer);
      if (dismissed && hovered !== dismissed && focused !== dismissed) dismissed = null;
      if (node === active || (node && node === dismissed)) return;
      timer = setTimeout(() => node ? show(node) : hide(), delay);
    };
    const over = (event: PointerEvent) => { hovered = find(event.target); choose(hovered || focused); };
    const out = (event: PointerEvent) => {
      if (popupContains(event.relatedTarget)) { clearTimeout(timer); return; }
      hovered = find(event.relatedTarget); choose(hovered || focused);
    };
    const popupOver = (event: PointerEvent) => { if (popupContains(event.target)) clearTimeout(timer); };
    const popupOut = (event: PointerEvent) => { if (popupContains(event.target)) out(event); };
    const focus = (event: FocusEvent) => {
      focused = find(event.target, true);
      if (focused && focused === active) show(focused); else choose(focused || hovered, 0);
    };
    const blur = (event: FocusEvent) => {
      if (describedControl) describe(describedControl, false);
      describedControl = null;
      focused = find(event.relatedTarget, true); choose(focused || hovered, 0);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { dismissed = active; hide(); } };
    page.addEventListener("pointerover", over);
    page.addEventListener("pointerout", out);
    page.addEventListener("focusin", focus);
    page.addEventListener("focusout", blur);
    document.addEventListener("pointerover", popupOver);
    document.addEventListener("pointerout", popupOut);
    document.addEventListener("keydown", escape);
    document.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    refresh.current = update;
    return () => {
      clearTimeout(timer); cancelAnimationFrame(frame); observer.disconnect();
      if (active) describe(active, false);
      if (describedControl) describe(describedControl, false);
      refresh.current = () => {};
      page.removeEventListener("pointerover", over);
      page.removeEventListener("pointerout", out);
      page.removeEventListener("focusin", focus);
      page.removeEventListener("focusout", blur);
      document.removeEventListener("pointerover", popupOver);
      document.removeEventListener("pointerout", popupOut);
      document.removeEventListener("keydown", escape);
      document.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [id, root]);
  useLayoutEffect(() => { refresh.current(); }, [revision]);
  useLayoutEffect(() => { if (anchor) tooltip.current?.forceAlign(); }, [anchor]);
  // Keep the proxy outside contentEditable: it must never enter selections or copied text.
  return createPortal(<Tooltip ref={tooltip} id={id} title={`${anchor?.length || 0} charact.`}
    open={!!anchor} trigger={[]} placement="right" fresh>
    <span data-rhese-tooltip-anchor="" aria-hidden="true" style={{ position: "fixed", pointerEvents: "none",
      left: anchor?.left || 0, top: anchor?.top || 0, width: anchor?.width || 0, height: anchor?.height || 0 }} />
  </Tooltip>, document.body);
}
